/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
  formatNumber,
  getChangeRateDirection,
  getEnabledRollUps,
  formatProductAnalyticsReportDates,
  formatProductAnalyticsTableDate,
  getChartColorsSubset,
  chartZAxisColor,
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './avginviewreport_styles';  
import LineChart from '../../../../../material/linechart/linechart';
import { isPercentageMetric } from '../../../helperfunction';

/* ========== ~~~~~~~~~~ TRIVIA AND POLLS ANALYTICS - AVERAGE IN VIEW REPORT ~~~~~~~~~~ ========== */
const AverageInViewReport = injectSheet(styles)((props) => {
  // props
  const { selectedInterval, setSelectedInterval } = props;

  // redux state
  const { overview, details, status } = useSelector((state) => state.triviaAndPolls.avgInView);
  const [selectedReport, setSelectedReport] = useState('avg-in-view');
  const [isLoadingReport, loadReport] = useState(false);
  const timerRef = useRef(null);
  const { filters } = useSelector((state) => state.app);
  const changeType = filters.compareTo;
  const isAvgInViewSectionSelected = selectedReport === 'avg-in-view';

  // component states
  const [avgInViewChartLegends, setAvgInViewChartLegends] = useState([]);
  const [totalEmbedLoadChartLegends, setTotalEmbedLoadChartLegends] = useState([]);
  const [showAvgInView, setShowAvgInView] = useState(false);
  const [enabledRollups, setEnabledRollups] = useState([]);
  const [selectedPeriodX, setSelectedPeriodX] = useState(null);

  // component constants
  const chartTableColors = getChartColorsSubset(2);
  const avgInViewProducts = [{ id: 'inViewPercentage', name: 'In View Percentage', color: chartTableColors[0] }];
  const totalEmbedLoadProducts = [{ id: 'pageLoadCount', name: 'Total Embed Load', color: chartTableColors[0] }];
  const totalDatesForInterval = details ? details[selectedInterval] : {};

  // when page gets loaded, then show total avgInView and totalEmbedLoad
  useEffect(() => {
    if (status === 'fulfilled' && overview) {
      showAggregateTotalAvgInView();
      showAggregateTotalEmbedLoad();
    }
  }, [overview]);

  // when mouse hover leaves the chart show back the net total aggregate
  useEffect(() => {
    if (showAvgInView) {
      if (isAvgInViewSectionSelected) showAggregateTotalAvgInView();
      else showAggregateTotalEmbedLoad();
    }
  }, [showAvgInView]);

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => clearTimeout(timerRef.current);
  }, []);

  // disable/enable intervals based on the number of dates
  useEffect(() => {
    if (status === 'fulfilled' && enabledRollups.length === 0) {
      const rollups = getEnabledRollUps(details, 'trivia');
      setEnabledRollups(rollups);
    } else {
      setEnabledRollups([]);
    }
  }, [status]);

  /**
   * show aggregate total for total avgInView
   */
  const showAggregateTotalAvgInView = () => {
    const productArr = avgInViewProducts.map((product) => ({
      ...product,
      value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
      changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
      checked:
        avgInViewChartLegends.length > 0
          ? avgInViewChartLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setAvgInViewChartLegends(productArr);
  };

  /**
   * show aggregate total for total embedLoad
   */
  const showAggregateTotalEmbedLoad = () => {
    const productArr = totalEmbedLoadProducts.map((product) => ({
      ...product,
      value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
      changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
      checked:
        totalEmbedLoadChartLegends.length > 0
          ? totalEmbedLoadChartLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setTotalEmbedLoadChartLegends(productArr);
  };

  const getBarChartKeys = () =>
    isAvgInViewSectionSelected
      ? avgInViewProducts.map((product) => product.name).reverse()
      : totalEmbedLoadProducts.map((product) => product.name).reverse();

  const getBarChartColors = () =>
    isAvgInViewSectionSelected
      ? avgInViewProducts.map((product) => product.color).reverse()
      : totalEmbedLoadProducts.map((product) => product.color).reverse();

  const renderAvgInViewLineChartLegends = () => {
    return avgInViewChartLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={`${legend.name}`}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...avgInViewChartLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setAvgInViewChartLegends(legends);
          }}
          value={formatNumber(legend.value, 'percent')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trivia'}
        />
      );
    });
  };

  const renderTotalEmbedLoadBarChartLegends = () => {
    return totalEmbedLoadChartLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={legend.name}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...totalEmbedLoadChartLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setTotalEmbedLoadChartLegends(legends);
          }}
          value={formatNumber(legend.value, 'number')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trivia'}
        />
      );
    });
  };

  const transformTotalEmbedLoadBarChartData = () => {
    let barChartData = [];
    barChartData = Object.keys(totalDatesForInterval).map((date, index) => {
      const dateLength = Object.keys(totalDatesForInterval).length;
      const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
      let item = { date: formattedDate };
      totalEmbedLoadProducts.forEach((product) => {
        const detailsDateData = details[selectedInterval][date];
        const checkedProducts = totalEmbedLoadChartLegends.filter((product) => product.checked === true);
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          item[product.name] = detailsDateData[product.id];
          item[`${product.name}Color`] = product.color;
          item['origDate'] = date;
          item['formattedTableDate'] = formatProductAnalyticsTableDate(
            date,
            selectedInterval,
            detailsDateData.startDate,
            detailsDateData.endDate,
          );
        }
      });
      return item;
    });
    return barChartData;
  };

  const transformAvgInViewLineChartData = () => {
    const lineChartData = [];
    const dateLength = Object.keys(totalDatesForInterval).length;
    if (details && details[selectedInterval]) {
      const checkedProducts = avgInViewChartLegends.filter((product) => product.checked === true);
      const items = [];
      avgInViewProducts.forEach((product) => {
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          Object.keys(details[selectedInterval]).forEach((date, index) => {
            const value = details[selectedInterval][date].inViewPercentage;
            // To allow duplicate x-axis labels
            let spaces = '';
            for (let i = 0; i < index; i++) spaces += ' ';
            const detailsDateData = details[selectedInterval][date];
            items.push({
              x: formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true).concat(spaces),
              y: value,
              origDate: date,
              formattedDate: formatProductAnalyticsTableDate(
                date,
                selectedInterval,
                detailsDateData.startDate,
                detailsDateData.endDate,
              ),
              productName: 'Avg In View',
            });
          });

          lineChartData.push({
            id: product.id,
            color: product.color,
            data: items,
          });
        }
      });
    }
    return lineChartData;
  };

  const handleHoverOnEmbedLoadBarChart = (barData) => {
    const date = barData.data ? barData.data.origDate : barData.origDate;
    const legends = [...totalEmbedLoadChartLegends];

    totalEmbedLoadProducts.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = totalEmbedLoadChartLegends.map((legend) => legend.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setTotalEmbedLoadChartLegends(legends);
    setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
    setShowAvgInView(false);
  };
  const handleHoverOnAvgInViewLineChart = (lineChartData) => {
    const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate;
    const legends = [...avgInViewChartLegends];
    avgInViewProducts.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = avgInViewChartLegends.map((product) => product.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setAvgInViewChartLegends(legends);
    setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`);
    setShowAvgInView(false);
  };

  const handleMouseLeave = () => {
    setShowAvgInView(true);
    setSelectedPeriodX('');
  };

  const monetizationBarChart = {
    indexBy: 'date',
    data: isAvgInViewSectionSelected ? transformAvgInViewLineChartData() : transformTotalEmbedLoadBarChartData(),
    keys: getBarChartKeys(),
    colors: getBarChartColors(),
    legends: isAvgInViewSectionSelected ? renderAvgInViewLineChartLegends() : renderTotalEmbedLoadBarChartLegends(),
  };

  const handleSelectReport = (report) => {
    loadReport(true);
    setSelectedReport(report);
    timerRef.current = setTimeout(() => loadReport(false), 1500);
  };

  const cardtoggles = (
    <CardToggles>
      <CardToggle
        label={'Avg In View'}
        compareType={changeType}
        metricType={'trivia'}
        percentChange={{
          value: overview?.period ? formatNumber(overview.period.inViewPercentageRateChange, 'percent') : 0,
          direction: overview?.period ? getChangeRateDirection(overview.period.inViewPercentageRateChange) : 'up',
        }}
        selected={selectedReport === 'avg-in-view'}
        handleClick={() => handleSelectReport('avg-in-view')}
        value={overview?.period ? formatNumber(overview.period.inViewPercentage, 'percent') : ''}
      />
      <CardToggle
        label={'Total Embed Load'}
        compareType={changeType}
        metricType={'trivia'}
        percentChange={{
          value: overview?.period ? formatNumber(overview.period.pageLoadCountRateChange, 'percent') : 0,
          direction: overview?.period ? getChangeRateDirection(overview.period.pageLoadCountRateChange) : 'up',
        }}
        selected={selectedReport === 'total-embed-load'}
        handleClick={() => handleSelectReport('total-embed-load')}
        value={overview?.period ? formatNumber(overview.period.pageLoadCount) : ''}
      />
    </CardToggles>
  );

  const formattedData = transformAvgInViewLineChartData();
  const hasSingleData = () => {
    let singleData = false;
    formattedData.forEach((item) => {
      if (item.data.length === 1) singleData = true;
    });
    return singleData;
  };

  //----------------------------------------main functions for line chart and barchart-----------------------------------------
  const barchart = (
    <BarChart
      indexBy={'date'}
      colors={monetizationBarChart.colors}
      keys={monetizationBarChart.keys}
      data={monetizationBarChart.data}
      period={selectedInterval}
      loading={status === 'pending' || isLoadingReport ? true : false}
      onHover={(data) => handleHoverOnEmbedLoadBarChart(data)}
      onMouseLeave={handleMouseLeave}
      reset={showAvgInView}
      zAxisColor={chartZAxisColor}
    />
  );

  const lineChart = (
    <LineChart
      enablePoints={hasSingleData()}
      data={formattedData}
      onHover={handleHoverOnAvgInViewLineChart}
      onMouseLeave={handleMouseLeave}
      reset={showAvgInView}
      yType={'avg-in-view'}
      useDashedLines={true}
      period={selectedInterval}
      isPercentageData={isPercentageMetric('VIEWABILITY')}
      loading={status === 'pending' || isLoadingReport ? true : false}
    />
  );

  const detailtable = status === 'fulfilled' ? (
    <DetailsTable
      period={selectedInterval}
      tableType={'trivia'}
      products={isAvgInViewSectionSelected ? avgInViewProducts : totalEmbedLoadProducts}
      data={details ? details[selectedInterval] : null}
      csvFileName={isAvgInViewSectionSelected ? 'Avg In View Report' : 'Total Embed Load Report'}
      dataType={isAvgInViewSectionSelected ? 'percent' : 'number'}
    />
  ) : []

  /**
   *
   * @returns if data is there or not to display on trivia page
   */
  const isNoData = () => {
    return (
      status === 'rejected' ||
      (status === 'fulfilled' && !overview?.period?.inViewPercentage) ||
      (status === 'fulfilled' && Object.keys(overview).length === 0)
    );
  };

  return (
    <div name={'avgInViewAndTotalEmbedLoadReportForTrivia'}>
      <ChartReportSection
        legendTitle={selectedPeriodX}
        title={'Avg In View/Total Embed Load'}
        tooltipContent={
          <div className={'tip-content'}>
            <div className={'tip-title'}>
              <h4>{isAvgInViewSectionSelected ? 'Avg In View' : 'Total Embed Load'}</h4>
            </div>
            <div className={'tip-desc'}>
              <p>
                {isAvgInViewSectionSelected
                  ? 'The Average In-View % is the number of Embed In-view events divided by Total Embed load events.'
                  : 'The Total Embed Load metric indicates the number of times the CEU unit has been loaded on the users page.'}
              </p>
            </div>
          </div>
        }
        cardtoggles={cardtoggles}
        chart={isAvgInViewSectionSelected ? lineChart : barchart}
        note={''}
        noData={isNoData()}
        enabledPeriods={enabledRollups}
        chartLegend={monetizationBarChart.legends}
        handleSelectPeriod={(period) => {
          setSelectedInterval(period);
          handleMouseLeave();
        }}
        selectedPeriod={selectedInterval}
        detailTable={detailtable}
      />
    </div>
  );
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AverageInViewReport;